<template>
  <!-- Lista dinámica de avales/seguros (S02): la cabecera es una fila más de la tarjeta (label + nota);
       al desplegar aparecen los botones y las filas. En edición los campos son inputs reales
       (select, fecha con calendario, numérico con €, texto); en reposo se muestran como texto. -->
  <div class="c-list">
    <div class="c-head" @click="expanded = !expanded">
      <span class="lbl">
        <b-icon pack="fas" :icon="expanded ? 'chevron-down' : 'chevron-right'" size="is-small"></b-icon>
        <span>{{ title }}</span>
      </span>
      <span class="val">
        <span class="c-note">{{ localRows.length }} {{ note }}</span>
        <span v-if="expanded" class="c-actions" @click.stop>
          <button class="c-btn add" @click="addRow">
            <b-icon pack="fas" icon="plus" size="is-small"></b-icon><span>{{ addLabel }}</span>
          </button>
          <button class="c-btn edit" :class="{ active: editing }" @click="editing = !editing">
            <b-icon pack="fas" :icon="editing ? 'check' : 'pen'" size="is-small"></b-icon>
            <span>{{ editing ? $t('prm_summary_s02_finish_edit') : editLabel }}</span>
          </button>
        </span>
      </span>
    </div>

    <div v-if="expanded" class="c-items">
      <div v-for="(row, i) in localRows" :key="row.id" class="c-row">
        <div class="c-row-head">
          <span class="c-row-title">{{ itemLabel }} {{ i + 1 }}</span>
          <button v-if="editing" class="c-remove" v-tip="removeLabel" @click="removeRow(i)">
            <b-icon pack="fas" icon="trash-can" size="is-small"></b-icon>
          </button>
        </div>
        <div class="c-cells">
          <div v-for="col in columns" :key="col.key" class="c-cell" :style="{ flex: col.flex || 1 }">
            <!-- Edición: input real según el tipo de campo. -->
            <template v-if="editing">
              <select v-if="col.type === 'select'" class="c-in" :value="row[col.key]"
                      @change="onCellSave(i, col.key, $event.target.value)">
                <option value=""></option>
                <option v-for="o in col.options" :key="o.value" :value="o.value">{{ o.label }}</option>
              </select>
              <input v-else-if="col.type === 'date'" type="date" class="c-in" :value="row[col.key]"
                     @change="onCellSave(i, col.key, $event.target.value)">
              <span v-else-if="col.type === 'number'" class="c-num">
                <input type="number" class="c-in" :value="row[col.key]" :placeholder="$t(col.label)"
                       @change="onCellSave(i, col.key, $event.target.value)">
                <span class="c-suffix">{{ currencySym }}</span>
              </span>
              <input v-else type="text" class="c-in" :value="row[col.key]" :placeholder="$t(col.label)"
                     @change="onCellSave(i, col.key, $event.target.value)">
            </template>
            <!-- Reposo: valor en texto. -->
            <span v-else class="c-val">{{ displayValue(col, row) }}</span>
            <span class="c-cell-lbl">{{ $t(col.label) }}</span>
          </div>
          <div class="c-cell pdf">
            <prm-versioned-file :file="fileFor(row.id)" :editing="editing" :project-id="projectId"
              :open="historyRow === row.id" :upload-url="uploadUrl(row.id)"
              @uploaded="$emit('files-changed')" @toggle-history="toggleHistory(row.id)"></prm-versioned-file>
            <span class="c-cell-lbl">{{ $t('prm_summary_s02_pdf_col') }}</span>
          </div>
        </div>

        <!-- Histórico documental de la fila: se despliega justo debajo del registro. -->
        <prm-file-history v-if="historyRow === row.id && fileFor(row.id)" class="c-history-wrap"
          :file="fileFor(row.id)" :base-url="baseUrl" :project-id="projectId" @close="historyRow = null"></prm-file-history>
      </div>
      <div v-if="!localRows.length" class="c-empty">{{ $t('prm_summary_empty') }}</div>
    </div>
  </div>
</template>

<script>
  import PrmVersionedFile from './versioned-file.vue'
  import PrmFileHistory from './file-history.vue'
  import { fmtLocalAmount, currencySymbol, fmtDate } from '../shared/format'
  import { tip } from '../shared/tip'

  let seq = 0
  // Id estable de fila (enlaza la fila del JSON con su PDF). Solo se genera al crear filas nuevas.
  function genId () {
    return 'r' + Date.now().toString(36) + (seq++).toString(36) + Math.random().toString(36).slice(2, 6)
  }

  export default {
    name: 'PrmContractList',

    components: { PrmVersionedFile, PrmFileHistory },

    directives: { tip },

    props: {
      title: { type: String, default: '' },
      addLabel: { type: String, default: '' },
      editLabel: { type: String, default: '' },
      itemLabel: { type: String, default: '' }, // titulo de cada fila (p. ej. "Aval")
      note: { type: String, default: '' },      // texto tras el contador (p. ej. "avales registrados ...")
      removeLabel: { type: String, default: '' },
      collection: { type: String, required: true }, // guarantee | insurance
      columns: { type: Array, required: true },
      rows: { type: Array, default: () => [] },
      files: { type: Object, default: () => ({}) }, // ficheros PDF por rowId
      projectId: { type: [Number, String], required: true },
      currency: { type: String, default: 'EUR' }, // moneda local del proyecto (importes del aval)
      resetKey: { type: Number, default: 0 }, // cambia al recargar -> re-inicializa la copia local
    },

    data: () => ({ expanded: false, editing: false, localRows: [], historyRow: null }),

    computed: {
      baseUrl () { return `/api/v2/project/${this.projectId}/dashboard-prm` },
      currencySym () { return currencySymbol(this.currency) },
    },

    watch: {
      // Solo re-inicializamos con una recarga real (no en cada emisión), para no perder foco.
      resetKey: { immediate: true, handler () { this.initLocal() } },
    },

    methods: {
      initLocal () {
        this.localRows = (this.rows || []).map((r) => ({ ...r, id: r.id || genId() }))
      },
      fileFor (rowId) { return (this.files || {})[rowId] || null },
      uploadUrl (rowId) { return `${this.baseUrl}/contract-files/${this.collection}/${rowId}` },
      // Valor mostrado en reposo según el tipo de campo.
      displayValue (col, row) {
        const v = row[col.key]
        if (v === '' || v === null || v === undefined) return '-'
        if (col.type === 'select') {
          const opt = (col.options || []).find((o) => String(o.value) === String(v))
          return opt ? opt.label : v
        }
        if (col.type === 'date') return fmtDate(v)
        if (col.type === 'number') return fmtLocalAmount(v, this.currency)
        return v
      },
      onCellSave (i, key, value) {
        this.localRows[i][key] = value
        this.emit()
      },
      addRow () {
        const row = { id: genId() }
        this.columns.forEach((c) => { row[c.key] = '' })
        this.localRows.push(row)
        this.expanded = true
        this.editing = true
        this.emit()
      },
      removeRow (i) {
        const row = this.localRows[i]
        this.localRows.splice(i, 1)
        this.emit()
        // Si la fila tenía PDF, avisamos para borrarlo del bucket.
        const f = row && this.fileFor(row.id)
        if (f) this.$emit('remove-file', f.id)
      },
      // Emite la lista limpia al padre (autosave), que la persiste.
      emit () {
        this.$emit('save', this.localRows.map((r) => ({ ...r })))
      },

      // Despliega/repliega el histórico documental de una fila.
      toggleHistory (rowId) { this.historyRow = this.historyRow === rowId ? null : rowId },
    },
  }
</script>

<style lang="scss" scoped>
  // Cabecera: misma altura y estilo que una fila kv normal de la tarjeta (label + valor).
  .c-head { display: flex; align-items: center; border-bottom: 1px solid #eef0f3; min-height: 25px; cursor: pointer; }
  .c-head .lbl {
    width: 180px; flex-shrink: 0; padding: 4px 10px; font-size: 9px; font-weight: 700; text-transform: uppercase;
    letter-spacing: .02em; color: #5a7896; background: #f6f8fa; align-self: stretch; display: flex; align-items: center; gap: 6px;
  }
  .c-head .val { flex: 1; display: flex; align-items: center; gap: 8px; padding: 3px 10px; font-size: 11px; color: #2c3a4d; min-width: 0; }
  .c-note { font-size: 10px; color: #9aa4b2; }
  .c-actions { margin-left: auto; display: inline-flex; gap: 6px; }
  .c-btn {
    display: inline-flex; align-items: center; gap: 4px; border: 1px solid #c3ccd8; background: #fff;
    color: #3d5570; font-size: 10px; font-weight: 600; padding: 3px 9px; border-radius: 5px; cursor: pointer; white-space: nowrap;
  }
  .c-btn:hover { background: #f6f9fc; }
  .c-btn.add { color: #1e6b3d; border-color: #a9d0b4; background: #eef7f0; }
  .c-btn.add:hover { background: #e2efe7; }
  .c-btn.edit.active { color: #1e6b3d; border-color: #a9d0b4; background: #eef7f0; }

  // Filas de items (al desplegar): compactas, con inputs (edición) o texto (reposo).
  .c-items { display: flex; flex-direction: column; gap: 6px; padding: 8px 10px; border-bottom: 1px solid #eef0f3; }
  .c-row { border: 1px solid #eef0f3; border-radius: 6px; padding: 7px 9px; }
  .c-row-head { display: flex; align-items: center; margin-bottom: 5px; }
  .c-row-title { font-size: 11px; font-weight: 700; color: #1e3046; }
  .c-remove { margin-left: auto; border: 0; background: transparent; color: #c0392b; cursor: pointer; opacity: .6; padding: 0; }
  .c-remove:hover { opacity: 1; }

  .c-cells { display: flex; flex-wrap: wrap; align-items: flex-start; gap: 8px 10px; }
  .c-cell { display: flex; flex-direction: column; gap: 3px; min-width: 90px; }
  .c-cell.pdf { flex: 0 0 auto; min-width: 0; }
  .c-cell-lbl { font-size: 8px; font-weight: 700; text-transform: uppercase; letter-spacing: .02em; color: #7a8798; }
  .c-val { font-size: 11px; color: #2c3a4d; min-height: 22px; display: flex; align-items: center; }

  // Inputs de edición.
  .c-in {
    width: 100%; font-size: 11px; color: #2c3a4d; border: 1px solid #d5dbe4; border-radius: 4px;
    padding: 3px 6px; background: #fff; outline: none; height: 26px; box-sizing: border-box;
  }
  .c-in:focus { border-color: #3a6bc8; box-shadow: 0 0 0 2px rgba(58, 107, 200, .12); }
  select.c-in { cursor: pointer; }
  .c-num { display: flex; align-items: center; gap: 4px; }
  .c-num .c-in { text-align: right; }
  .c-suffix { font-size: 11px; color: #5a7896; font-weight: 600; }
  .c-empty { text-align: center; color: #9aa4b2; font-size: 11px; padding: 8px; }
  // Histórico documental desplegado bajo la fila.
  .c-history-wrap { margin-top: 6px; }
</style>
